import React, { useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm, SubmitHandler } from 'react-hook-form'
import * as Yup from 'yup'

import { CreateLocationRequestDTO } from '@/core/api/dtos/request/location/create'
import { UpdateLocationRequestDTO } from '@/core/api/dtos/request/location/update'
import { Location } from '@/core/api/entities/location'
import { useTimezones } from '@/core/hooks/misc/useTimezones'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { PhoneInput } from '@/shared/components/atoms/PhoneNumber'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Textarea } from '@/shared/components/atoms/TextArea'
import { CountrySelector } from '@/shared/components/molecules/CountrySelector/CountrySelector'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface UpdateLocationFormData {
	name: string
	type: 'own' | 'franchise'
	country: string
	city: string
	address: string
	zip_code: string
	manager: string
	email: string
	phone_number: string
	timezone: string
	additional_information: string
}

interface UpdateLocationModalProps {
	visible: boolean
	location: Location | null
	onClose: () => void
	onConfirm: (data: UpdateLocationRequestDTO | CreateLocationRequestDTO) => void
	isUpdating: boolean
	mode?: 'create' | 'edit'
}

const UpdateLocationModal: React.FC<UpdateLocationModalProps> = ({
	visible,
	location,
	onClose,
	onConfirm,
	isUpdating,
	mode = 'edit',
}) => {
	const { t } = useTranslation('common')

	// Fetch timezones
	const { data: timezones = [] } = useTimezones()

	// Default values for the form
	const defaultValues = React.useMemo(
		() => ({
			name: mode === 'edit' ? location?.name || '' : '',
			type: (mode === 'edit' ? location?.type : 'own') as 'own' | 'franchise',
			country: mode === 'edit' ? location?.country || '' : '',
			city: mode === 'edit' ? location?.city || '' : '',
			address: mode === 'edit' ? location?.address || '' : '',
			zip_code: mode === 'edit' ? location?.zipCode || '' : '',
			manager: mode === 'edit' ? location?.manager || '' : '',
			email: mode === 'edit' ? location?.email || '' : '',
			phone_number: mode === 'edit' ? location?.phoneNumber || '' : '',
			timezone: mode === 'edit' ? location?.timezone || '' : '',
			additional_information:
				mode === 'edit' ? location?.additionalInformation || '' : '',
		}),
		[location, mode]
	)

	// Create validation schema using Yup
	const validationSchema = Yup.object().shape({
		name: Yup.string().required(t('validation.required')),
		type: Yup.string().oneOf(['own', 'franchise']).default(defaultValues.type),
		country: Yup.string()
			.required(t('validation.required'))
			.default(defaultValues.country),
		city: Yup.string()
			.required(t('validation.required'))
			.default(defaultValues.city),
		address: Yup.string()
			.required(t('validation.required'))
			.default(defaultValues.address),
		zip_code: Yup.string()
			.required(t('validation.required'))
			.default(defaultValues.zip_code),
		manager: Yup.string()
			.required(t('validation.required'))
			.default(defaultValues.manager),
		email: Yup.string()
			.email(t('validation.email'))
			.required(t('validation.required'))
			.default(defaultValues.email),
		phone_number: Yup.string()
			.required(t('validation.required'))
			.default(defaultValues.phone_number),
		timezone: Yup.string()
			.required(t('validation.required'))
			.default(defaultValues.timezone),
		additional_information: Yup.string().default(
			defaultValues.additional_information
		),
	})

	const {
		control,
		handleSubmit,
		formState: { errors: _errors },
		reset,
	} = useForm<UpdateLocationFormData>({
		defaultValues,
		resolver: yupResolver(validationSchema),
	})

	// Reset form when location changes or mode changes
	useEffect(() => {
		if (mode === 'edit' && location) {
			reset(defaultValues)
		} else if (mode === 'create') {
			reset(defaultValues)
		}
	}, [location, mode, reset, defaultValues])

	// Reset form when modal closes
	useEffect(() => {
		if (!visible) {
			reset(defaultValues)
		}
	}, [visible, reset, defaultValues])

	const onSubmit: SubmitHandler<UpdateLocationFormData> = (data) => {
		// Transform form data to DTO format
		const updateData: UpdateLocationRequestDTO = {
			name: data.name,
			type: data.type,
			country: data.country,
			city: data.city,
			address: data.address,
			zip_code: data.zip_code,
			manager: data.manager,
			email: data.email,
			phone_number: data.phone_number,
			timezone: data.timezone,
			additional_information: data.additional_information || null,
		}
		onConfirm(updateData)
	}

	// Prepare timezone options
	const timezoneOptions = timezones.map((timezone) => ({
		value: timezone.value,
		label: timezone.label,
	}))

	return (
		<Dialog open={visible} onOpenChange={onClose}>
			<DialogContent
				className="sm:max-w-[800px] max-h-[80vh] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
			>
				<div className="px-6 py-6 border-b">
					<DialogTitle className="text-2xl">
						{mode === 'create'
							? t('entities.location.create_title')
							: t('entities.location.edit_title')}
					</DialogTitle>
				</div>

				<form
					id="update-location-form"
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col flex-1 min-h-0"
				>
					<div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
						{/* Location information */}
						<div className="space-y-2">
							<h3 className="text-lg font-semibold">Location information</h3>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<Label className="mb-1 block">
									{t('entities.location.update.form.name.label')}*
								</Label>
								<Controller
									name="name"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<div>
											<Input
												{...field}
												placeholder={t(
													'entities.location.update.form.name.placeholder'
												)}
												disabled={isUpdating}
												className="h-10 text-sm bg-input border-input"
											/>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<Label className="mb-1 block">
									{t('entities.location.update.form.country.label')}*
								</Label>
								<Controller
									name="country"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<div>
											<CountrySelector
												value={field.value as any}
												onChange={field.onChange}
												placeholder={t(
													'entities.location.update.form.country.placeholder'
												)}
												disabled={isUpdating}
												className="h-10 text-sm bg-input border-input"
											/>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>

							{/* City */}
							<div>
								<Label className="mb-1 block">
									{t('entities.location.update.form.city.label')}*
								</Label>
								<Controller
									name="city"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<div>
											<Input
												{...field}
												placeholder={t(
													'entities.location.update.form.city.placeholder'
												)}
												disabled={isUpdating}
												className="h-10 text-sm"
											/>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{/* Address */}
							<div>
								<Label className="mb-1 block">
									{t('entities.location.update.form.address.label')}*
								</Label>
								<Controller
									name="address"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<div>
											<Input
												{...field}
												placeholder={t(
													'entities.location.update.form.address.placeholder'
												)}
												disabled={isUpdating}
												className="h-10 text-sm"
											/>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>

							{/* Zip Code */}
							<div>
								<Label className="mb-1 block">
									{t('entities.location.update.form.zip_code.label')}*
								</Label>
								<Controller
									name="zip_code"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<div>
											<Input
												{...field}
												placeholder={t(
													'entities.location.update.form.zip_code.placeholder'
												)}
												disabled={isUpdating}
												className="h-10 text-sm"
											/>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>
						</div>

						{/* Phone Number and Timezone */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<Label className="mb-1 block">
									{t('entities.location.update.form.phone_number.label')}*
								</Label>
								<Controller
									name="phone_number"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<div>
											<PhoneInput
												value={field.value}
												onChange={field.onChange}
												placeholder={t(
													'entities.location.update.form.phone_number.placeholder'
												)}
												disabled={isUpdating}
												className="h-10 text-sm"
											/>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>

							{/* Timezone */}
							<div>
								<Label className="mb-1 block">
									{t('entities.location.update.form.timezone.label')}*
								</Label>
								<Controller
									name="timezone"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<div>
											<Select
												value={field.value}
												onValueChange={field.onChange}
												disabled={isUpdating}
											>
												<SelectTrigger className="h-10 w-full text-sm">
													<SelectValue
														placeholder={t(
															'entities.location.update.form.timezone.placeholder'
														)}
													/>
												</SelectTrigger>
												<SelectContent>
													{timezoneOptions.map((option) => (
														<SelectItem key={option.value} value={option.value}>
															{option.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>
						</div>

						{/* Manager information */}
						<div className="space-y-2 pt-4">
							<h3 className="text-lg font-semibold">Manager information</h3>
							<p className="text-sm text-muted-foreground">
								{t('entities.location.update.manager_info_description')}
							</p>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{/* Manager */}
							<div>
								<Label className="mb-1 block">
									{t('entities.location.update.form.manager.label')}*
								</Label>
								<Controller
									name="manager"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<div>
											<Input
												{...field}
												placeholder={t(
													'entities.location.update.form.manager.placeholder'
												)}
												disabled={isUpdating}
												className="h-10 text-sm"
											/>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>

							{/* Email */}
							<div>
								<Label className="mb-1 block">
									{t('entities.location.update.form.email.label')}*
								</Label>
								<Controller
									name="email"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<div>
											<Input
												{...field}
												type="email"
												placeholder={t(
													'entities.location.update.form.email.placeholder'
												)}
												disabled={isUpdating}
												className="h-10 text-sm"
											/>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>
						</div>

						{/* Additional Information */}
						<div>
							<Label className="mb-1 block">
								{t(
									'entities.location.update.form.additional_information.label'
								)}
							</Label>
							<Controller
								name="additional_information"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<div>
										<Textarea
											{...field}
											placeholder={t(
												'entities.location.update.form.additional_information.placeholder'
											)}
											disabled={isUpdating}
											rows={3}
											value={field.value || ''}
											className="text-sm bg-background border-border text-foreground resize-none"
										/>
										{error && <ErrorMessage error={error.message ?? ''} />}
									</div>
								)}
							/>
						</div>
					</div>

					{/* Footer */}
					<div className="px-6 py-6 border-t flex justify-end gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							disabled={isUpdating}
							className="rounded-full px-6"
						>
							{t('actions.cancel')}
						</Button>
						<Button
							type="submit"
							variant="default"
							disabled={isUpdating}
							className="rounded-full px-6"
						>
							{t('actions.save')}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	)
}

export default UpdateLocationModal
